import * as Yup from 'yup';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { OpenModalHookType } from '@hooks/useOpenModal';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from 'react-redux';
import { RootReducer } from '@store/index';
import { getBase64FromInputFile } from '@utils/Base64';
import { PaymentMethods, SalesChannel } from '@api/SalesChannels/SalesChannel';

interface ModalSaveSalesChannelProps {
    modalSaveSalesChannel: OpenModalHookType;
    salesChannel?: SalesChannel;
    onSaveSalesChanel: (salesChannel: SalesChannel) => void;
}

const saveSalesChannelSchema = Yup.object({
    id: Yup.string(),
    name: Yup.string().required('Nombre requerido'),
    paymentMethods: Yup.array()
        .min(1, 'Debes de elegir al menos un metodo de pago')
        .required('Metodo de pago requerido'),
    branchOfficesAvailable: Yup.array().min(1, 'Debes de elegir al menos una sucursal').required('Sucursal requerida')
});

export const ModalSaveSalesChannel: React.FunctionComponent<ModalSaveSalesChannelProps> = (props: any) => {
    const { modalSaveSalesChannel, salesChannel, onSaveSalesChanel } = props;
    // Branch offices come from the shared catalog slice; we just add the "all" option.
    const { branchOffices: branchOfficesCatalog } = useSelector((state: RootReducer) => state.krispis);
    const branchOffices = useMemo(
        () => [...branchOfficesCatalog, { id: 'all', name: 'Todas las sucursales' }],
        [branchOfficesCatalog]
    );
    const [photo, setPhoto] = useState<string | undefined>(undefined);

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        formState: { errors }
    } = useForm<SalesChannel>({
        resolver: yupResolver(saveSalesChannelSchema)
    });
    const handleClose = () => {
        modalSaveSalesChannel.toggle();
    };

    const onSaveSalesChannelForm: SubmitHandler<SalesChannel> = async (values) => {
        reset();
        onSaveSalesChanel(values);
        handleClose();
    };

    const uploadPhoto = async (event: any) => {
        const base64Photo = await getBase64FromInputFile(event);
        setPhoto(base64Photo);
        setValue('photoSrc', base64Photo);
    };

    useEffect(() => {
        if (modalSaveSalesChannel.modalOpen) {
            reset({
                id: salesChannel?.id || undefined,
                name: salesChannel?.name || '',
                paymentMethods: salesChannel?.paymentMethods || [PaymentMethods.cash],
                branchOfficesAvailable: salesChannel?.branchOfficesAvailable || ['all'],
                creditCardCommission: salesChannel?.creditCardCommission || 0
            });
            setPhoto(salesChannel?.photoSrc);
        }
    }, [modalSaveSalesChannel.modalOpen]);

    return (
        <Dialog
            PaperProps={{
                style: { borderRadius: 15, padding: 4 }
            }}
            open={modalSaveSalesChannel.modalOpen}
            onClose={handleClose}
            aria-labelledby="modal-save-sales-channel-title"
            aria-describedby="modal-save-sales-channel-description"
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle id="modal-save-sales-channel-title">
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight={600}>
                        Nuevo canal
                    </Typography>
                    <IconButton color="secondary" onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <form id="add-sales-channel-form" onSubmit={handleSubmit(onSaveSalesChannelForm)}>
                    <Grid container spacing={2}>
                        <Grid item xs={4}>
                            <Stack
                                height="100%"
                                width="100%"
                                bgcolor="lightgrey"
                                borderRadius={5}
                                justifyContent="center"
                                alignItems="center"
                                component="label"
                                overflow="hidden"
                                sx={{ cursor: 'pointer' }}
                            >
                                {photo && <Box alignSelf="center" width="100%" component="img" src={photo} />}
                                {!photo && <Typography>Subir icono</Typography>}
                                <input type="file" hidden onChange={uploadPhoto} />
                            </Stack>
                        </Grid>
                        <Grid item xs={8}>
                            <Stack spacing={2}>
                                <Controller
                                    name="name"
                                    control={control}
                                    render={({ field: { onChange, value } }) => (
                                        <TextField
                                            fullWidth
                                            required
                                            label="Nombre"
                                            variant="outlined"
                                            value={value}
                                            onChange={onChange}
                                            error={!!errors.name}
                                            helperText={errors.name && errors.name.message}
                                        />
                                    )}
                                />
                                <FormControl fullWidth>
                                    <InputLabel id="payment-methods-label">Medios de pago</InputLabel>
                                    <Controller
                                        name="paymentMethods"
                                        control={control}
                                        render={({ field: { onChange, value } }) => (
                                            <Select
                                                labelId="payment-methods-label"
                                                id="payment-methods"
                                                multiple
                                                value={value}
                                                label="Medios de pago"
                                                onChange={onChange}
                                                error={!!errors.paymentMethods}
                                            >
                                                {Object.values(PaymentMethods).map((method, index) => (
                                                    <MenuItem key={index} value={method}>
                                                        {method}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        )}
                                    />
                                </FormControl>
                                <FormControl fullWidth>
                                    <InputLabel id="branch-offices-label">Disponible en</InputLabel>
                                    <Controller
                                        name="branchOfficesAvailable"
                                        control={control}
                                        render={({ field: { onChange, value } }) => (
                                            <Select
                                                labelId="branch-offices-label"
                                                id="branch-offices"
                                                multiple
                                                value={value}
                                                label="Sucursal(es)"
                                                onChange={onChange}
                                                error={!!errors.branchOfficesAvailable}
                                            >
                                                {branchOffices.map((branchOffice, index) => (
                                                    <MenuItem key={index} value={branchOffice.id}>
                                                        {branchOffice.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        )}
                                    />
                                </FormControl>
                                <Controller
                                    name="creditCardCommission"
                                    control={control}
                                    render={({ field: { onChange, value } }) => (
                                        <TextField
                                            fullWidth
                                            required
                                            type="number"
                                            label="Comision por pago con tarjeta"
                                            variant="outlined"
                                            value={value}
                                            onChange={onChange}
                                            error={!!errors.creditCardCommission}
                                            helperText={
                                                errors.creditCardCommission && errors.creditCardCommission.message
                                            }
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <Typography variant="body1">%</Typography>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    )}
                                />
                            </Stack>
                        </Grid>
                    </Grid>
                </form>
            </DialogContent>
            <DialogActions>
                <Button fullWidth color="primary" variant="contained" type="submit" form="add-sales-channel-form">
                    Guardar
                </Button>
            </DialogActions>
        </Dialog>
    );
};
